import { Inject, Injectable } from '@angular/core'
import { Params, Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { ROUTER_NAVIGATED } from '@ngrx/router-store'
import { Action, select, Store } from '@ngrx/store'
import { from, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { DRR_BASE_HREF } from '../../shared/shared.config'
import { PayloadedAction } from '../../shared/types'
import { generateVehicleSlug } from '../../shared/utils'
import { AAAStore } from '../../store/root-reducer'
import { selectUrl } from '../../store/router.selectors'
import { selectDefaultVehicle, selectEligibility, selectIsSignedIn, selectIsVehicleChangeAllowed } from '../auth/auth.selectors'
import { selectActiveCallStatus } from '../dashboard/calls-statuses/call-status.selectors'
import { selectNeedsTow } from '../issue/issue.selectors'
import { RouteTypes } from '../main.routes'
import {
  ASSIGN_DEFAULT_VEHICLE,
  ASSIGN_ELIGIBILITY_VEHICLE,
  ASSIGN_EXISTING_VEHICLE,
  assignExistingVehicle,
  SET_VEHICLE,
  setEligibilityVehicle,
  setVehicle,
} from '../member/member.actions'
import { selectIsMotorcycleEligible, selectIsMotorhomeEligible, selectMemberVehicles } from '../member/member.selectors'
import { VehicleData } from '../member/member.types'
import { needsEnrichment, resetVehicleDetails } from '../member/member.utils'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import events from '../tagging/events'
import { TaggingService } from '../tagging/tagging.service'
import { AdobeEventTypes } from '../tagging/tagging.types'
import { StepTypes, VehicleSections } from '../ui/ui.types'
import { currentEditStepLocation } from '../wizard/wizard.selectors'
import { WizardService } from '../wizard/wizard.service'
import {
  AUTOMATED_VEHICLE_ANALYTICS,
  clearEditMetadata,
  CREATE_NEW_VEHICLE,
  EDIT_VEHICLE,
  ENRICH_VEHICLE_DETAILS,
  enrichVehicleDetailsSuccess,
  notifyEnrichVehicleDetailsFailure,
  notifySetVehicleDataFailure,
  notifyVehicleDetailsFailure,
  notifyVehicleMakesFailure,
  notifyVehicleModelsFailure,
  notifyVehicleYearsFailure,
  requestEnrichVehicleDetails,
  requestVehicleByPlate,
  requestVehicleDetails,
  SET_VEHICLE_DETAILS,
  SET_VEHICLE_MAKE,
  SET_VEHICLE_MODEL_AND_TYPE,
  SET_VEHICLE_YEAR,
  setIsVehicleEditing,
  setVehicleStep,
  VEHICLE_BY_PLATE,
  VEHICLE_DETAILS,
  VEHICLE_MAKES,
  VEHICLE_MODELS,
  VEHICLE_YEARS,
  vehicleByPlateSuccess,
  vehicleDetailsSuccess,
  vehicleMakesSuccess,
  vehicleModelsSuccess,
  vehicleYearsSuccess,
} from './vehicle.actions'
import { VehicleEditState } from './vehicle.reducer'
import { createGetVehicleDetailsByModel, selectEditMetadata, selectWorkingVehicle } from './vehicle.selectors'
import { VehicleService } from './vehicle.service'
import {
  EditVehicleParams,
  PlateToVinResponse,
  VehicleEditMetadata,
  VehicleMakes,
  VehicleModels,
  VehicleYears,
} from './vehicle.types'
import { isVehiclePresent, PAGE_NAME_MAPPING, VehicleUtils } from './vehicle.utils'

const NEXT_DESTINATIONS = {
  [CREATE_NEW_VEHICLE]: { step: StepTypes.VEHICLE, section: VehicleSections.MAKES },
  [SET_VEHICLE_YEAR]: { step: StepTypes.VEHICLE, section: VehicleSections.MAKES },
  [SET_VEHICLE_MAKE]: { step: StepTypes.VEHICLE, section: VehicleSections.MODELS },
  [SET_VEHICLE_MODEL_AND_TYPE]: { step: StepTypes.VEHICLE, section: VehicleSections.DETAILS },
  [SET_VEHICLE_DETAILS]: { step: StepTypes.ISSUE },
}

@Injectable()
export class VehicleEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private router: Router,
    private _vehicleService: VehicleService,
    private errorReportingService: ErrorReportingService,
    private wizardService: WizardService,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
    private vehicleUtils: VehicleUtils,
    @Inject(DRR_BASE_HREF) private drrBaseHref: string
  ) {}

  handleNavigation = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map(
        (action: PayloadedAction) => action.payload.routerState.root.queryParams
      ),
      filter((params: Params) => params.step === StepTypes.VEHICLE),
      map(({ section }) => {
        const pageName = PAGE_NAME_MAPPING[section] || events.vehicle.PAGE_NAME_VEHICLE_LIST
        this.taggingService.setPageLoadEvent({ pageType: events.vehicle.PAGE_TYPE, pageName })
        return setVehicleStep({ payload: { step: section || '' } })
      })
    )
  )

  handleVehicleEdit = createEffect(() =>
    this.actions$.pipe(
      ofType(EDIT_VEHICLE),
      switchMap((action: PayloadedAction<EditVehicleParams>) => {
        this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], {
          queryParams: {
            step: StepTypes.VEHICLE,
            section: action.payload.section,
          },
        })
        return of(setIsVehicleEditing({ payload: Boolean(action.payload.vehicle) }))
      })
    )
  )

  loadVehicleYears$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VEHICLE_YEARS.REQUEST),
      switchMap(() =>
        this._vehicleService.getYears$().pipe(
          map((years: VehicleYears) => vehicleYearsSuccess({ payload: years })),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifyVehicleYearsFailure
            )
          )
        )
      )
    )
  )

  loadVehicleMakes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VEHICLE_MAKES.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectWorkingVehicle)),
        this.store$.pipe(select(selectIsMotorcycleEligible)),
        this.store$.pipe(select(selectIsMotorhomeEligible))
      ),
      switchMap(
        ([action, workingVehicle, isMotorcycleEligible, isMotorhomeEligible]: [PayloadedAction, VehicleEditState, boolean, boolean]) =>
          this._vehicleService.getMakes$(
            +(action.payload || workingVehicle.year), {
              showMotorcycles: isMotorcycleEligible,
              showMotorhomes: isMotorhomeEligible,
              showTypicalVehicles: true
            }
          ).pipe(
            map((makes: VehicleMakes) =>
              vehicleMakesSuccess({ payload: makes })
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                notifyVehicleMakesFailure
              )
            )
          )
      )
    )
  )

  loadVehicleModels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VEHICLE_MODELS.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectWorkingVehicle)),
        this.store$.pipe(select(selectIsMotorcycleEligible)),
        this.store$.pipe(select(selectIsMotorhomeEligible))
      ),
      switchMap(
        ([
          action, workingVehicle, isMotorcycleEligible, isMotorhomeEligible
        ]: [
          PayloadedAction, VehicleEditState, boolean, boolean
        ]) => {
          const payload = {
            ...workingVehicle,
            ...action.payload,
          }

          return this._vehicleService.getModels$(+payload.year, payload.make, {
            showMotorcycles: isMotorcycleEligible,
            showMotorhomes: isMotorhomeEligible,
            showTypicalVehicles: true
          }).pipe(
            map((models: VehicleModels) =>
              vehicleModelsSuccess({ payload: models })
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                notifyVehicleModelsFailure
              )
            )
          )
        }
      )
    )
  )

  loadVehicleDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VEHICLE_DETAILS.REQUEST),
      switchMap((action: PayloadedAction) => {
        let { year, make, model } = action.payload

        if (make.includes('Motorhome/') || model.includes('Motorhome/')) {
          [make, model] = this.vehicleUtils.normalizeMotorHome([make, model])
        }

        return this._vehicleService.getVehicleDetails$(year, make, model).pipe(
          map((response) => vehicleDetailsSuccess({
            payload: {
              [generateVehicleSlug(action.payload)]: response.details ?? []
            }
          })),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifyVehicleDetailsFailure
            )
          )
        )
      })
    )
  )

  getVehicleByPlate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestVehicleByPlate>>(VEHICLE_BY_PLATE.REQUEST),
      switchMap(({ payload: { tag, state } }) =>
        this._vehicleService.getVehicle$(tag, state).pipe(
          switchMap((response: PlateToVinResponse) => ([
            vehicleByPlateSuccess({ payload: response })
          ])
          ),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              [vehicleByPlateSuccess({ payload: { vehicles: [] } })]
            )
          )
        )
      )
    )
  )

  // We have an existing vehicle to assign to the call.  This just tags it in and moves on.
  assignExistingVehicle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReturnType<typeof assignExistingVehicle>>(ASSIGN_EXISTING_VEHICLE),
        switchMap((action) =>
          of(action).pipe(
            withLatestFrom(
              this.store$.pipe(select(selectActiveCallStatus)),
              this.store$.pipe(select(currentEditStepLocation)),
              this.store$.pipe(select(selectNeedsTow)),
              this.store$.pipe(select(createGetVehicleDetailsByModel)),
            ),
            filter(([_, activeCallStatus]) => activeCallStatus === null),
            map(([_, _2, currentStepUrl, needsTow, getVehicleDetailsByModel]) => {
              const vehicleDetails = getVehicleDetailsByModel.memoized(generateVehicleSlug(action.payload))
              if (!vehicleDetails || vehicleDetails.length === 0) {
                this.store$.dispatch(requestVehicleDetails({
                  payload: {
                    year: Number(action.payload.year),
                    make: action.payload.make,
                    model: action.payload.model
                  }
                }))
              }
              // if we came from any edition (right panel) we will redirect the user
              // to the page whom triggered the edition
              if (currentStepUrl && !needsTow) {
                return this.wizardService.backToEditUrl(currentStepUrl)
              }

              // If we didn't trigger the edition, but instead we manually select something
              // from the wizard, we will go to towing only when is needed if not, will go back to summary
              const stepsection = needsTow ? StepTypes.TOWING : StepTypes.SUBMIT
              const params: any = {
                step: StepTypes.ISSUE
              }

              this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], { queryParams: params })
            }),
            catchError((error) =>
              from(this.errorReportingService.notifyErrorObservable(error, []))
            )
          )
        )
      ),
    { dispatch: false }
  )

  assignEligibilityVehicle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ASSIGN_ELIGIBILITY_VEHICLE),
        withLatestFrom(
          this.store$.select(selectEligibility),
          this.store$.select(selectMemberVehicles),
        ),
        filter(([_, eligibility]) =>
          eligibility?.contractIdentityData?.['vehicle']),
        switchMap(([_, eligibility, memberVehicles]) => {
          const rawVehicle = eligibility.contractIdentityData['vehicle'];
          const enrichedMatch = memberVehicles.find(
            v => v.isEnriched && v.year == rawVehicle.year && v.make === rawVehicle.make && v.model === rawVehicle.model
          );
          const vehicle = enrichedMatch
            ? { ...rawVehicle, fuelType: enrichedMatch.fuelType, vehicleType: enrichedMatch.vehicleType, isEnriched: true }
            : rawVehicle;
          const actions: Action[] = [setEligibilityVehicle({ payload: vehicle })];
          if (needsEnrichment(vehicle)) {
            actions.push(requestEnrichVehicleDetails({ payload: { vehicle, addMemberVehicle: false } }));
          }
          return from(actions);
        })
      )
  )

  assignDefaultVehicle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ASSIGN_DEFAULT_VEHICLE),
        withLatestFrom(
          this.store$.select(selectDefaultVehicle),
          this.store$.select(selectMemberVehicles),
        ),
        filter(([_, defaultVehicle]) =>
          isVehiclePresent(defaultVehicle)),
        switchMap(([_, defaultVehicle, memberVehicles]) => {
          const enrichedMatch = memberVehicles.find(
            v => v.isEnriched && v.year == defaultVehicle.year && v.make === defaultVehicle.make && v.model === defaultVehicle.model
          );
          const vehicle = enrichedMatch
            ? { ...defaultVehicle, fuelType: enrichedMatch.fuelType, vehicleType: enrichedMatch.vehicleType, isEnriched: true }
            : defaultVehicle;
          const actions: Action[] = [setEligibilityVehicle({ payload: vehicle })];
          if (needsEnrichment(vehicle)) {
            actions.push(requestEnrichVehicleDetails({ payload: { vehicle, addMemberVehicle: false } }));
          }
          return from(actions);
        })
      )
  )

  // This effect handles edits - once data is stored, this determines whether we are in the middle
  // of editing a vehicle, and if so, where to go when done.
  setVehicleData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CREATE_NEW_VEHICLE,
        SET_VEHICLE_YEAR,
        SET_VEHICLE_MAKE,
        SET_VEHICLE_MODEL_AND_TYPE,
        SET_VEHICLE_DETAILS
      ),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectEditMetadata)),
            this.store$.pipe(select(selectUrl)),
          ),
          filter(
            ([payloadedAction, metadata, currentUrl]: [
              PayloadedAction,
              VehicleEditMetadata,
              string,
            ]) =>
              this.navigationNextLocation(
                payloadedAction.type,
                metadata.completionUrl,
                currentUrl
              )
          ),
          withLatestFrom(this.store$.pipe(select(selectWorkingVehicle))),
          mergeMap(([_, vehicleEditState]: [never, VehicleEditState]) => {
            const actions: Array<any> = []

            // Sometimes we are editing an existing vehicle with an ID.  At other times we are "editing"
            // an earlier stage of a vehicle which is still new.
            if (vehicleEditState && vehicleEditState.id) {
              actions.push(
                setVehicle({ payload: { ...resetVehicleDetails(vehicleEditState) as VehicleData } })
              )
            }

            return actions
          }),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              notifySetVehicleDataFailure
            )
          )
        )
      )
    )
  )

  // This effect complements the above setVehicleData$, which takes care of most saving/routing -
  // this effect is needed if setVehicleDetails is carried out in main flow, rather than by a vehicle edit.
  setVehicleDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_VEHICLE_DETAILS),
      withLatestFrom(
        this.store$.pipe(select(selectWorkingVehicle)),
        this.store$.pipe(select(selectEditMetadata))
      ),
      // If a return URL is set, SetVehicle is taken care of by the main flow
      filter(
        ([_, _2, metadata]: [never, VehicleEditState, VehicleEditMetadata]) =>
          !metadata.completionUrl
      ),
      map(([_, workingVehicle, _2]: [never, VehicleEditState, never]) =>
        setVehicle({ payload: { ...resetVehicleDetails(workingVehicle) as VehicleData } })
      )
    )
  )

  /**
   * We need to check if the vehicle information has been set before
   * redirecting the user to the towing view, this will avoid
   * a conflict between the steps state
   */
  handleSetVehicle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ReturnType<typeof setVehicle>>(SET_VEHICLE),
        withLatestFrom(this.store$.pipe(select(selectEditMetadata))),
        // If a return URL is set, SetVehicle is taken care of by the main flow
        filter(([_, metadata]: [PayloadedAction, VehicleEditMetadata]) => !metadata.completionUrl),
        withLatestFrom(
          this.store$.pipe(select(selectNeedsTow)),
        ),
        tap(([_, needsTow]) => {
          const queryParams = {
            step: StepTypes.ISSUE,
          }
          this.router.navigate([this.drrBaseHref, RouteTypes.STEPS], { queryParams })
        })
      ),
    { dispatch: false }
  )

  // If the user navigates away, forget about editing - don't want later vehicle changes to
  // navigate weirdly
  clearEditOnCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_NAVIGATED),
      map(
        (action: PayloadedAction) => action.payload.routerState.root.queryParams
      ),
      filter(
        (params: Params) => params.step !== StepTypes.VEHICLE || !params.section
      ),
      withLatestFrom(this.store$.pipe(select(selectEditMetadata))),
      filter(
        ([_, metadata]: [PayloadedAction, VehicleEditMetadata]) =>
          !!metadata.completionUrl
      ),
      map(clearEditMetadata)
    )
  )

  simulateVehicleAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTOMATED_VEHICLE_ANALYTICS),
      withLatestFrom(this.store$.pipe(select(selectIsVehicleChangeAllowed))),
      filter(([_, isVehicleChangeAllowed]: [PayloadedAction, boolean]) => !isVehicleChangeAllowed),
      tap((_) => {
        this.taggingService.setAutomatedEvent(
          events.vehicle.VEHICLE_PRELOADED_SELECT,
          events.vehicle.VEHICLE_SUSPICIOUS_PAGE_TYPE
        )
        this.taggingService.setAutomatedEvent(
          events.vehicle.VEHICLE_COMPLETE,
          events.vehicle.VEHICLE_SUSPICIOUS_PAGE_TYPE
        )
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.CTA,
          eventValue: events.vehicle.VEHICLE_PRELOADED_SELECT
        })
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.CTA,
          eventValue: events.vehicle.VEHICLE_COMPLETE
        })
      })
    ),
  { dispatch: false }
  )

  navigationNextLocation(action, completionUrl, currentUrl = null): boolean {
    // Ugly special case for makes...
    // 1. Don't navigate "back" if back is the same as the current location...
    if (
      completionUrl &&
      completionUrl !== currentUrl &&
      action !== SET_VEHICLE_MAKE
    ) {
      this.router.navigateByUrl(completionUrl)
      return true
    }

    const queryParams = NEXT_DESTINATIONS[action]

    this.router.navigate([], { queryParams })
    // 2. But if we are finishing the edit, still tell the caller
    return completionUrl === currentUrl
  }

  requestVehicleEnrichment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestEnrichVehicleDetails>>(ENRICH_VEHICLE_DETAILS.REQUEST),
      withLatestFrom(this.store$.select(selectIsSignedIn)),
      // Details endpoint requires auth; skipping keeps the vehicle needing
      // enrichment so it is enriched after sign-in instead of failing with 401
      filter(([_, isSignedIn]) => isSignedIn),
      tap((_) => {
        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.vehicle.VEHICLE_ENRICHMENT_REQUEST,
        });
      }),
      mergeMap(([action]) =>
        this._vehicleService.getVehicleDetails$(
          Number(action.payload.vehicle.year),
          action.payload.vehicle.make,
          action.payload.vehicle.model
        ).pipe(
          map((response) => enrichVehicleDetailsSuccess({
            payload: {
              enrichedDetails: response,
              vehicle: action.payload.vehicle,
              addMemberVehicle: action.payload.addMemberVehicle,
            }
          })),
          catchError((_) => of(notifyEnrichVehicleDetailsFailure({
            payload: {
              vehicle: action.payload.vehicle,
              addMemberVehicle: action.payload.addMemberVehicle,
            }
          })))
        )
      )
    )
  )

}
